import { TemplateEntityPeriodicityEnum } from '@zaoblako/shared/common/enums/template.entity.periodicity.enum';
import { TemplateEntityPeriodicityEnumI18n } from '@zaoblako/shared/common/i18n/template.entity.periodicity.enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CommonDtoKeysEnum } from '@zaoblako/shared/common/enums/common-dto-keys.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';

import * as yup from 'yup';

/** Planning unit create/update request body */
export interface PlanningUnitFormInterface {
  /**
   * Concrete date of running. Must be defined only when
   * {@link PlanningUnitFormInterface.periodicity} property equals
   * {@link TemplateEntityPeriodicityEnum.ONCE}.
   */
  runDate?: Date;
  /**
   * Day of week number. Must be defined only when
   * {@link PlanningUnitFindDto.periodicity} property equals
   * {@link TemplateEntityPeriodicityEnum.WEEKLY}.
   */
  dayOfWeek?: number;
  /**
   * Day of month number. Must be defined only when
   * {@link PlanningUnitFindDto.periodicity} property equals
   * {@link TemplateEntityPeriodicityEnum.MONTHLY} or
   * {@link TemplateEntityPeriodicityEnum.QUARTERLY}.
   */
  dayOfMonth?: number;
  /**
   * Month of quarter number. Must be defined only when
   * {@link PlanningUnitFindDto.periodicity} property equals
   * {@link TemplateEntityPeriodicityEnum.QUARTERLY}.
   */
  monthOfQuarter?: number;
  /**
   * Month of year number. Must be defined only when
   * {@link PlanningUnitFindDto.periodicity} property equals
   * {@link TemplateEntityPeriodicityEnum.ANNUALLY}.
   */
  monthOfYear?: number;
  /**
   * Running periodicity unit. Determines which fields must be
   * filled in and how the start date should be formed.
   */
  periodicity: TemplateEntityPeriodicityEnum;
  /** Time of day value */
  time?: string;
}

/** Planning unit create/update request body - DTO schema */
export const PlanningUnitFormDto = SerdeDtoClass.create<PlanningUnitFormInterface>(CommonDtoKeysEnum.PLANNING_UNIT_FORM_DTO, {
  runDate: {
    optional: context => (<PlanningUnitFormInterface>context.value)?.periodicity !== TemplateEntityPeriodicityEnum.ONCE,
    label: 'Дата запуска',
    serializer: yup.date(),
  },
  dayOfWeek: {
    optional: context => (<PlanningUnitFormInterface>context.value)?.periodicity !== TemplateEntityPeriodicityEnum.WEEKLY,
    label: 'День недели',
    serializer: yup
      .number()
      .min(1, 'День недели должен быть числом от 1 до 7')
      .max(7, 'День недели должен быть числом от 1 до 7'),
  },
  dayOfMonth: {
    optional: context => ![TemplateEntityPeriodicityEnum.MONTHLY, TemplateEntityPeriodicityEnum.QUARTERLY].includes(
      (<PlanningUnitFormInterface>context.value)?.periodicity,
    ),
    label: 'День месяца',
    serializer: yup
      .number()
      .min(1, 'День месяца должен быть числом от 1 до 31')
      .max(31, 'День месяца должен быть числом от 1 до 31'),
  },
  monthOfQuarter: {
    optional: context => (<PlanningUnitFormInterface>context.value)?.periodicity !== TemplateEntityPeriodicityEnum.QUARTERLY,
    label: 'Месяц квартала',
    serializer: yup
      .number()
      .min(1, 'Месяц в квартале должен быть числом от 1 до 3')
      .max(3, 'Месяц в квартале должен быть числом от 1 до 3'),
  },
  monthOfYear: {
    optional: context => (<PlanningUnitFormInterface>context.value)?.periodicity !== TemplateEntityPeriodicityEnum.ANNUALLY,
    label: 'Месяц года',
    serializer: yup
      .number()
      .min(1, 'Месяц в году должен быть числом от 1 до 12')
      .max(12, 'Месяц в году должен быть числом от 1 до 12'),
  },
  periodicity: {
    label: 'Периодичность',
    serializer: commonEnumValidator(TemplateEntityPeriodicityEnum, TemplateEntityPeriodicityEnumI18n),
  },
  time: {
    optional: true,
    label: 'Время',
    serializer: yup
      .string()
      .matches(
        /^(?:([0-2]{1}\d{1}:[0-5]{1}\d{1})|([0-2]{1}\d{1}:[0-5]{1}\d{1}:[0-5]{1}\d{1}))$/i,
        'Время должно быть задано в виде строки в формате "ЧЧ:ММ" или "ЧЧ:ММ:СС"',
      ),
  },
});
